import { useCallback, useRef, useState } from 'react';

// Below this, a press is a click, not a drag.
const DRAG_THRESHOLD_PX = 4;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Lets a fixed element be dragged anywhere on screen. The offset lives in state only, so it lasts
 * across client-side navigation and resets on a reload.
 */
const useDragOffset = () => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const hasMovedRef = useRef(false);

  const onPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        origin: offset,
        rect: event.currentTarget.getBoundingClientRect(),
      };
      hasMovedRef.current = false;
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [offset],
  );

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (!hasMovedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    hasMovedRef.current = true;
    // Clamped against where the element started, so all of it stays on screen.
    setOffset({
      x: drag.origin.x + clamp(dx, -drag.rect.left, window.innerWidth - drag.rect.right),
      y: drag.origin.y + clamp(dy, -drag.rect.top, window.innerHeight - drag.rect.bottom),
    });
  }, []);

  const onPointerEnd = useCallback(() => {
    dragRef.current = null;
  }, []);

  // A drag still ends in a click on the element; the caller skips that one.
  const consumeDrag = useCallback(() => {
    const wasDragged = hasMovedRef.current;
    hasMovedRef.current = false;
    return wasDragged;
  }, []);

  return {
    offset,
    consumeDrag,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp: onPointerEnd, onPointerCancel: onPointerEnd },
  };
};

export default useDragOffset;
